import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../lib/api';
import { 
  Trash, 
  Plus, 
  Minus, 
  ShoppingCart,
  ArrowLeft,
  ArrowRight
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

const Cart = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { cart, fetchCart, removeFromCart, clearCart, cartTotal, loading } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCart();
    }
  }, [isAuthenticated, fetchCart]);

  const handleRemove = async (productId) => {
    try {
      await removeFromCart(productId);
      toast.success('Item removed from cart');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCheckout = async () => {
    if (cart.items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setCheckingOut(true);
    try {
      const response = await api.post('/checkout/cart', {
        origin_url: window.location.origin
      });

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed');
      setCheckingOut(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
        <div className="text-center">
          <ShoppingCart className="w-16 h-16 text-zinc-600 mx-auto mb-4" weight="duotone" />
          <h2 className="font-outfit text-2xl text-white mb-2">Login Required</h2>
          <p className="text-zinc-400 mb-6">Please login to view your cart</p>
          <Link to="/login">
            <Button className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
              Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="cart-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button 
            variant="ghost" 
            onClick={() => navigate('/shop')}
            className="text-zinc-400 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Continue Shopping
          </Button>
          <h1 className="font-outfit text-3xl sm:text-4xl font-light text-white">
            Your <span className="text-[#D4AF37]">Cart</span>
          </h1>
        </motion.div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="bg-[#121212] border border-white/5 p-6 animate-pulse">
                <div className="flex gap-4">
                  <div className="w-24 h-24 bg-zinc-800" />
                  <div className="flex-1">
                    <div className="h-4 bg-zinc-800 rounded w-1/2 mb-2" />
                    <div className="h-4 bg-zinc-800 rounded w-1/4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : cart.items.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <ShoppingCart className="w-20 h-20 text-zinc-700 mx-auto mb-4" weight="duotone" />
            <h2 className="font-outfit text-xl text-white mb-2">Your cart is empty</h2>
            <p className="text-zinc-400 mb-6">Add some items to get started</p>
            <Link to="/shop">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
                Browse Shop
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map((item, index) => (
                <motion.div
                  key={item.product_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-[#121212] border border-white/5 p-4"
                  data-testid={`cart-item-${item.product_id}`}
                >
                  <div className="flex gap-4">
                    <img 
                      src={item.product?.image_url || 'https://images.unsplash.com/photo-1634032188532-f11af97817ab?w=100'}
                      alt={item.product?.name}
                      className="w-24 h-24 object-cover"
                    />
                    <div className="flex-1">
                      <h3 className="font-outfit text-white font-medium">{item.product?.name}</h3>
                      <p className="text-sm text-zinc-500">{item.product?.category}</p>
                      {item.variation && (
                        <p className="text-xs text-zinc-400 mt-1">
                          {Object.entries(item.variation).map(([k, v]) => `${k}: ${v}`).join(', ')}
                        </p>
                      )}
                      <div className="flex items-center justify-between mt-4">
                        <div className="flex items-center gap-2">
                          <span className="text-zinc-400 text-sm">Qty: {item.quantity}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-mono text-[#D4AF37]">
                            ${((item.product?.price || 0) * item.quantity).toFixed(2)}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemove(item.product_id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            data-testid={`remove-item-${item.product_id}`}
                          >
                            <Trash className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}

              <Button
                variant="outline"
                onClick={clearCart}
                className="border-zinc-700 text-zinc-400 hover:text-white"
              >
                <Trash className="w-4 h-4 mr-2" />
                Clear Cart
              </Button>
            </div>

            {/* Order Summary */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-1"
            >
              <div className="bg-[#121212] border border-white/5 p-6 sticky top-24">
                <h2 className="font-outfit text-xl text-white mb-6">Order Summary</h2>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Subtotal</span>
                    <span className="text-white font-mono">${cartTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Shipping</span>
                    <span className="text-zinc-500">Calculated at checkout</span>
                  </div>
                  <div className="border-t border-white/10 pt-3 mt-3">
                    <div className="flex justify-between">
                      <span className="text-white font-medium">Total</span>
                      <span className="text-[#D4AF37] font-mono text-xl">${cartTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleCheckout}
                  disabled={checkingOut || cart.items.length === 0}
                  className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C158] py-6 gold-glow"
                  data-testid="checkout-btn"
                >
                  {checkingOut ? (
                    <span className="animate-spin w-5 h-5 border-2 border-black border-t-transparent rounded-full" />
                  ) : (
                    <>
                      Checkout
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-xs text-zinc-500 text-center mt-4">
                  Secure checkout powered by Stripe
                </p>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
